import { useCallback } from 'react'
import auth from '../../../api/auth/hooks'
import { setModulePermissions } from '../../AppPermissionsSwitcher/utils/module'

interface PermissionAction {
    permission: string
    name: string
}

interface RowWithAgent {
    agent: {
        id: number
        agencyOffice: { id: number } | null
    }
}

/**
 * Hook checks user permissions on a given row.
 *
 * Returns a callback that checks permissions via `setModulePermissions`. It's passed as
 * `selectRow.enableRow` in `useDataTable`, which wires it to v8's `enableRowSelection` — so
 * `row.getCanSelect()` returns `false` and the checkbox is rendered disabled and skipped on
 * "select all" automatically via `checkboxColumn.tsx`.
 */
const useRowPermission = <TRow extends RowWithAgent>(module: string, permissionAction: PermissionAction) => {
    const { data: currentAgent } = auth.CurrentAgent.useQuery({
        select: (data) => ({
            id: data.id,
            agencyOffice: data.agencyOffice,
            agentPermissions: data.agentPermissions,
        }),
    })

    return useCallback(
        (row: TRow) => {
            // TODO: check if/when Contact agent field on BE is renamed to primaryAgent
            const isAgentOwnership = row.agent?.id === currentAgent?.id
            const isAgentOffice = row.agent?.agencyOffice?.id === currentAgent?.agencyOffice?.id

            const { hasModulePermission } = setModulePermissions({
                agentPermissions: currentAgent?.agentPermissions,
                isAgentOwnership,
                isAgentOffice,
                module,
                action: permissionAction,
                permissionName: undefined,
            })

            return hasModulePermission
        },
        [currentAgent?.id, currentAgent?.agencyOffice?.id, currentAgent?.agentPermissions, module, permissionAction]
    )
}

export default useRowPermission
